/**
 * KOPS-460: Event-Specific Landing Page
 * aisearchportal.com/events/:eventId
 * Each sponsor gets their own branded registration link
 */

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt, faMapMarkerAlt, faClock, faUsers,
  faChalkboardTeacher, faTools, faRocket, faCheckCircle,
  faBuilding, faArrowRight, faLaptop, faGraduationCap, faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../helpers/api';

interface EventData {
  id: string;
  title: string;
  series_name: string;
  category: string;
  description: string;
  event_date: string;
  event_time: string;
  duration_minutes: number;
  location_name: string;
  location_address: string;
  max_capacity: number;
  current_registrations: number;
  sponsor_name: string | null;
  sponsor_contact_name: string | null;
  ticket_price: number;
  seats_remaining: number;
  is_full: boolean;
  ce_school_name: string | null;
  ce_school_address: string | null;
  ce_school_phone: string | null;
  ce_adre_school_number: string | null;
  ce_course_approval_number: string | null;
  ce_credit_hours: number | null;
  ce_pending_approval: boolean;
  ce_instructor_name: string | null;
  ce_admin_name: string | null;
  ce_admin_email: string | null;
}

const EventDetailPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState<EventData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', brokerage: '', licenseNumber: '', optIn: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{ success: boolean; message: string } | null>(null);

  const colors = {
    navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
    gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
    white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF'
  };

  useEffect(() => {
    fetchEvent();
    // Handle return from Stripe payment
    const paid = searchParams.get('paid');
    const sessionId = searchParams.get('session_id');
    if (paid === 'true' && sessionId) {
      setSubmitting(true);
      axiosInstance.post('/events/verify-payment', { sessionId }).then(res => {
        setSubmitResult({ success: true, message: res.data.message || 'Payment confirmed! Check your email for confirmation.' });
      }).catch(err => {
        setSubmitResult({ success: false, message: err.response?.data?.error || 'Payment verification failed. Please contact support.' });
      }).finally(() => setSubmitting(false));
    }
    if (searchParams.get('cancelled') === 'true') {
      setSubmitResult({ success: false, message: 'Payment was cancelled. You can try again below.' });
    }
  }, [eventId]);

  const fetchEvent = async () => {
    try {
      const response = await axiosInstance.get(`/events/${eventId}`);
      if (response.data.success) {
        setEvent(response.data.event);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const isPaid = event ? event.ticket_price > 0 : false;

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!event) return;
    setSubmitting(true);
    setSubmitResult(null);

    try {
      if (isPaid) {
        // Paid event: create Stripe checkout session
        const response = await axiosInstance.post('/events/checkout', {
          eventId: event.id,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          brokerage: formData.brokerage || undefined,
          licenseNumber: formData.licenseNumber || undefined
        });

        if (response.data.checkoutUrl) {
          window.location.href = response.data.checkoutUrl;
          return; // Redirect to Stripe
        }
      } else {
        // Free event: register directly
        const response = await axiosInstance.post('/events/register', {
          eventId: event.id,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          brokerage: formData.brokerage || undefined,
          licenseNumber: formData.licenseNumber || undefined
        });

        setSubmitResult({ success: true, message: response.data.message });
        setFormData({ fullName: '', email: '', phone: '', brokerage: '', licenseNumber: '', optIn: false });
        fetchEvent();
      }
    } catch (err: any) {
      setSubmitResult({
        success: false,
        message: err.response?.data?.error || 'Registration failed. Please try again.'
      });
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    // Append timezone to prevent UTC shifting - dates are stored in Arizona time
    const date = new Date(dateStr.includes('T') && !dateStr.includes('Z') ? dateStr + '-07:00' : dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      timeZone: 'America/Phoenix'
    });
  };

  if (loading) {
    return (
      <div style={{ background: colors.navy, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner animation="border" style={{ color: colors.gold }} />
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div style={{ background: colors.navy, color: colors.white, minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <Container className="text-center py-5">
          <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '48px', color: colors.white, marginTop: '100px' }}>Event Not Found</h1>
          <p style={{ color: colors.muted, fontSize: '18px' }}>This event may have ended or the link is incorrect.</p>
          <Button href="/events" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, marginTop: '20px' }}>
            View All Events
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ background: colors.navy, color: colors.white, fontFamily: "'DM Sans', -apple-system, sans-serif", minHeight: '100vh' }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Navigation */}
      <nav style={{ background: `linear-gradient(135deg, ${colors.navy3} 0%, #0d1e38 100%)`, borderBottom: `2px solid ${colors.gold}`, padding: '16px 0' }}>
        <Container className="d-flex justify-content-between align-items-center">
          <a href="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '2px', color: colors.white }}>
              {typeof window !== 'undefined' && (window.location.hostname.includes('kairosiq.ai')) ? (
                <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span> <span style={{ color: colors.muted, fontSize: '18px' }}>AI</span></>
              ) : (
                <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '18px' }}>.com</span></>
              )}
            </span>
          </a>
          <div className="d-flex gap-3 align-items-center">
            <a href="/events" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '14px', fontWeight: 600 }}>All Events</a>
            {event?.category !== 'ce_course' && (
              <Button size="sm" onClick={() => window.location.href = '/agent-portal/signup'} style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                Get Started
              </Button>
            )}
          </div>
        </Container>
      </nav>

      {/* Event Hero */}
      <section style={{ background: `linear-gradient(180deg, ${colors.navy} 0%, ${colors.navy2} 100%)`, padding: '60px 0 40px' }}>
        <Container>
          <div className="text-center">
            <p style={{ color: colors.gold2, fontSize: '14px', fontWeight: 700, letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '16px' }}>
              {event.series_name} {event.category === 'ce_course' ? '| Continuing Education' : '| Seminar'}
            </p>
            <h1 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '56px', letterSpacing: '3px', marginBottom: '24px', lineHeight: 1.1, color: colors.white }}>
              {event.title}
            </h1>

            {/* Event Details Card */}
            <div style={{ background: colors.navy3, borderLeft: `4px solid ${colors.gold}`, padding: '24px 30px', maxWidth: '600px', margin: '0 auto', borderRadius: '8px', textAlign: 'left' }}>
              <Row>
                <Col sm={6}>
                  <p className="mb-2" style={{ color: colors.off }}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" style={{ color: colors.gold2 }} />
                    {formatDate(event.event_date)}
                  </p>
                  <p className="mb-2" style={{ color: colors.off }}>
                    <FontAwesomeIcon icon={faClock} className="me-2" style={{ color: colors.gold2 }} />
                    {event.event_time}
                  </p>
                </Col>
                <Col sm={6}>
                  <p className="mb-2" style={{ color: colors.off }}>
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" style={{ color: colors.gold2 }} />
                    {event.location_name}
                  </p>
                  {(() => {
                    const addr = event.location_address || '';
                    const parts = addr.split(',').map((p: string) => p.trim());
                    const street = parts.length >= 3 ? parts.slice(0, parts.length - 2).join(', ') : parts[0] || '';
                    const cityStateZip = parts.length >= 3 ? parts.slice(parts.length - 2).join(', ') : parts.slice(1).join(', ');
                    return (
                      <>
                        <p className="mb-0" style={{ color: colors.muted, fontSize: '14px', paddingLeft: '24px' }}>
                          {street}
                        </p>
                        {cityStateZip && (
                          <p className="mb-2" style={{ color: colors.muted, fontSize: '14px', paddingLeft: '24px' }}>
                            {cityStateZip}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </Col>
              </Row>
              {event.sponsor_name && (
                <p className="mb-0 mt-2" style={{ color: colors.gold2, fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faBuilding} className="me-2" />
                  {event.category === 'ce_course' ? 'Venue provided by' : 'Hosted by'} {event.sponsor_name}
                  {event.sponsor_contact_name && ` — ${event.sponsor_contact_name}`}
                </p>
              )}
              <div className="mt-2">
                <span style={{ fontSize: '14px', color: event.is_full ? '#e74c3c' : '#27ae60', fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  {event.is_full ? 'EVENT IS FULL' : `${event.seats_remaining} seats remaining`}
                </span>
              </div>

              {/* CE School Info — inline with venue (ADRE R4-28-502) */}
              {event.category === 'ce_course' && event.ce_school_name && (
                <div style={{ marginTop: '16px', padding: '14px 18px', background: 'rgba(201,149,42,0.08)', borderRadius: '8px', borderLeft: `3px solid ${colors.gold}` }}>
                  {event.ce_instructor_name && <p style={{ color: colors.gold2, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' }}>
                    <FontAwesomeIcon icon={faGraduationCap} className="me-2" />Instructor: {event.ce_instructor_name}
                  </p>}
                  <p style={{ color: colors.off, fontSize: '15px', fontWeight: 600, margin: '0 0 4px 0' }}>{event.ce_school_name}</p>
                  {event.ce_school_phone && <p style={{ color: colors.muted, fontSize: '13px', margin: '0 0 4px 0' }}>{event.ce_school_phone}</p>}
                  {event.ce_admin_name && <p style={{ color: colors.muted, fontSize: '13px', margin: '0 0 2px 0' }}>Administrator: {event.ce_admin_name}</p>}
                  {event.ce_admin_email && <p style={{ color: colors.muted, fontSize: '13px', margin: '0 0 8px 0' }}>{event.ce_admin_email}</p>}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '12px' }}>
                    {event.ce_credit_hours && <span style={{ color: colors.gold2, fontWeight: 600 }}><FontAwesomeIcon icon={faCheckCircle} className="me-1" />{event.ce_credit_hours} CE Credits</span>}
                    {event.ce_adre_school_number && <span style={{ color: colors.muted }}>ADRE #{event.ce_adre_school_number}</span>}
                  </div>
                  {!!event.ce_pending_approval && (
                    <p style={{ color: colors.gold2, fontSize: '11px', marginTop: '8px', marginBottom: 0 }}>⚠️ This course is pending ADRE approval. CE credits will be awarded upon approval.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </Container>
      </section>

      {/* Description + What You'll Learn */}
      {event.description && (
        <section style={{ background: colors.navy2, padding: '40px 0' }}>
          <Container>
            <Row className="justify-content-center">
              <Col md={8}>
                <div style={{ fontSize: '16px', color: colors.off, lineHeight: 1.8, textAlign: 'left' }}>
                  {event.description.split('\n').map((line: string, i: number) => (
                    <p key={i} style={{ margin: '8px 0' }}>{line}</p>
                  ))}
                </div>
              </Col>
            </Row>
            <Row className="g-4 mt-3 justify-content-center">
              {(event.category === 'ce_course' ? [
                { icon: faGraduationCap, text: 'Approved CE credit hours' },
                { icon: faShieldAlt, text: 'Fair Housing & AI ethics focus' },
                { icon: faChalkboardTeacher, text: 'Case studies & real-world scenarios' },
                { icon: faCheckCircle, text: 'Certificate of completion provided' }
              ] : [
                { icon: faChalkboardTeacher, text: 'Hands-on, interactive session' },
                { icon: faLaptop, text: event.category === 'workshop' ? 'Bring your laptop' : 'Live demonstrations included' },
                { icon: faCheckCircle, text: 'Walk away with actionable tools' },
                { icon: faTools, text: 'Fair Housing compliance built in' }
              ]).map((item, i) => (
                <Col sm={6} md={3} key={i} className="text-center">
                  <FontAwesomeIcon icon={item.icon} style={{ color: colors.gold2, fontSize: '24px', marginBottom: '8px' }} />
                  <p style={{ color: colors.muted, fontSize: '13px', margin: 0 }}>{item.text}</p>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Registration Form */}
      <section style={{ background: colors.navy, padding: '60px 0' }}>
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <h2 style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '42px', textAlign: 'center', marginBottom: '12px', letterSpacing: '2px', color: colors.white }}>
                Reserve Your <span style={{ color: colors.gold2 }}>Seat</span>
              </h2>
              <p style={{ textAlign: 'center', color: colors.muted, marginBottom: '32px' }}>
                {event.category === 'ce_course'
                  ? isPaid
                    ? <><span style={{ color: colors.gold2, fontWeight: 700, fontSize: '24px' }}>${event.ticket_price}</span> per seat. Continuing education credits provided.</>
                    : 'Continuing education credits provided.'
                  : isPaid
                    ? <><span style={{ color: colors.gold2, fontWeight: 700, fontSize: '24px' }}>${event.ticket_price}</span> per seat. Bring your laptop — this is interactive.</>
                    : event.category === 'seminar'
                      ? 'Free to attend. Space is limited — register to reserve your spot.'
                      : 'Free to attend. Bring your laptop — this is interactive.'
                }
              </p>

              {submitResult && (
                <Alert variant={submitResult.success ? 'success' : 'danger'} className="mb-4">
                  {submitResult.success && <FontAwesomeIcon icon={faCheckCircle} className="me-2" />}
                  {submitResult.message}
                </Alert>
              )}

              {event.is_full ? (
                <Alert variant="warning" className="text-center">
                  This event is at full capacity. Please check back for future sessions at <a href="/events">/events</a>.
                </Alert>
              ) : (
                <Form onSubmit={handleRegister} style={{ background: colors.navy3, borderRadius: '12px', padding: '32px', border: `1px solid ${colors.navy}` }}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Full Name *</Form.Label>
                    <Form.Control required value={formData.fullName} onChange={e => setFormData({ ...formData, fullName: e.target.value })} placeholder="Jane Smith" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Email *</Form.Label>
                    <Form.Control required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} placeholder="jane@example.com" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Phone *</Form.Label>
                    <Form.Control required value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} placeholder="(480) 555-1234" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ color: colors.muted, fontSize: '13px', fontWeight: 600 }}>Brokerage <span style={{ color: colors.muted, fontWeight: 400 }}>(optional)</span></Form.Label>
                    <Form.Control value={formData.brokerage} onChange={e => setFormData({ ...formData, brokerage: e.target.value })} placeholder="Your Brokerage" style={{ background: colors.navy2, color: colors.white, border: `1px solid ${colors.navy}` }} />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      id="opt-in-detail"
                      checked={formData.optIn}
                      onChange={e => setFormData({ ...formData, optIn: e.target.checked })}
                      label={
                        <span style={{ color: colors.muted, fontSize: '13px' }}>
                          I agree to receive event updates and communications from KairosIQ AI, Inc. You may unsubscribe at any time.
                        </span>
                      }
                    />
                  </Form.Group>

                  <Button type="submit" disabled={submitting || !formData.optIn} className="w-100" size="lg" style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 700, fontSize: '16px' }}>
                    {submitting
                      ? <><Spinner size="sm" className="me-2" />Processing...</>
                      : isPaid
                        ? <>Register & Pay ${event.ticket_price} <FontAwesomeIcon icon={faArrowRight} className="ms-2" /></>
                        : <>Register Now <FontAwesomeIcon icon={faArrowRight} className="ms-2" /></>
                    }
                  </Button>
                </Form>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* CE Course Information & Compliance (ADRE R4-28-502) */}
      {event.category === 'ce_course' && (
        <section style={{ background: colors.navy3, padding: '30px 0' }}>
          <Container>
            <div style={{ maxWidth: '700px', margin: '0 auto' }}>
              {/* School Disclosure — Required by ADRE R4-28-502(D) */}
              {event.ce_school_name && (
                <div style={{ padding: '20px 24px', background: colors.navy2, borderLeft: `4px solid ${colors.gold}`, borderRadius: '4px', marginBottom: '16px' }}>
                  {event.ce_instructor_name && (
                    <p style={{ color: colors.off, fontSize: '14px', fontWeight: 700, marginBottom: '8px' }}>
                      <FontAwesomeIcon icon={faGraduationCap} className="me-2" style={{ color: colors.gold2 }} />
                      Instructor: {event.ce_instructor_name}
                    </p>
                  )}
                  <div style={{ fontSize: '14px', color: colors.off, lineHeight: 1.8 }}>
                    <div style={{ fontWeight: 600, fontSize: '15px' }}>{event.ce_school_name}</div>
                    {event.ce_school_phone && <div style={{ color: colors.muted }}>{event.ce_school_phone}</div>}
                    {event.ce_admin_name && <div style={{ color: colors.muted }}>Administrator: {event.ce_admin_name}</div>}
                    {event.ce_admin_email && <div style={{ color: colors.muted }}>{event.ce_admin_email}</div>}
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '16px', fontSize: '13px' }}>
                    {event.ce_credit_hours && (
                      <span style={{ color: colors.gold2, fontWeight: 600 }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-1" />{event.ce_credit_hours} CE Credit Hours
                      </span>
                    )}
                    {event.ce_adre_school_number && (
                      <span style={{ color: colors.muted }}>ADRE School #{event.ce_adre_school_number}</span>
                    )}
                  </div>
                  {!!event.ce_pending_approval && (
                    <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(201,149,42,0.1)', borderRadius: '4px', fontSize: '12px', color: colors.gold2 }}>
                      ⚠️ This course is pending ADRE approval. CE credits will be awarded upon approval.
                    </div>
                  )}
                </div>
              )}

              {/* Compliance Disclaimer */}
              <div style={{ padding: '16px 24px', background: colors.navy2, borderLeft: `4px solid rgba(201,149,42,0.3)`, borderRadius: '4px' }}>
                <p style={{ color: colors.muted, fontSize: '12px', lineHeight: 1.7, margin: 0 }}>
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" style={{ color: colors.gold2 }} />
                  This is a Continuing Education course provided in accordance with Arizona Department of Real Estate (ADRE) requirements. The venue provider listed above is not a product sponsor. No product offerings, sales pitches, or commercial solicitations will be made during this CE session. Course content is strictly educational and focused on professional development.
                </p>
              </div>
            </div>
          </Container>
        </section>
      )}

      {/* Quote */}
      <section style={{ background: colors.navy2, padding: '40px 0' }}>
        <Container className="text-center">
          <p style={{ fontStyle: 'italic', fontSize: '18px', color: colors.off, maxWidth: '600px', margin: '0 auto' }}>
            "The future of real estate isn't about AI replacing professionals — it's about professionals who use AI replacing those who don't."
          </p>
          <p style={{ color: colors.gold2, marginTop: '10px', fontSize: '14px', fontWeight: 600 }}>
            — Michael A. Boykin Jr., M.S. | Author, AI Without Fear
          </p>
        </Container>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', padding: '40px 0 20px', borderTop: `1px solid ${colors.navy3}` }}>
        <Container>
          <Row>
            <Col md={6}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                {typeof window !== 'undefined' && (window.location.hostname.includes('kairosiq.ai')) ? (
                  <>KAIROS<span style={{ color: colors.gold2 }}>IQ</span> <span style={{ color: colors.muted, fontSize: '14px' }}>AI</span></>
                ) : (
                  <>AI<span style={{ color: colors.gold2 }}>Search</span>Portal<span style={{ color: colors.muted, fontSize: '14px' }}>.com</span></>
                )}
              </span>
              <p style={{ color: colors.muted, fontSize: '13px', marginTop: '8px' }}>
                {typeof window !== 'undefined' && (window.location.hostname.includes('kairosiq.ai')) ? 'KairosIQ AI, Inc. — Chandler, Arizona' : 'Built by Arizona Licensed Brokers. Powered by KairosIQ AI, Inc.'}
              </p>
            </Col>
            <Col md={6} className="text-md-end">
              <p style={{ color: colors.muted, fontSize: '13px' }}>
                KairosIQ AI, Inc. | Chandler, Arizona<br />
                (520) 251-9595 | support@kairosiq.ai
              </p>
            </Col>
          </Row>
          <hr style={{ borderColor: colors.navy3, margin: '20px 0' }} />
          <p style={{ color: colors.muted, fontSize: '11px', textAlign: 'center', margin: 0 }}>
            AI estimates. Agents decide.
          </p>
        </Container>
      </footer>
    </div>
  );
};

export default EventDetailPage;
